import {
  VcnCardInUseStatus,
  VcnCardStatus,
  type VcnManagementCardFilters,
  type VcnManagementBenefitFilters,
} from './types'
import { VCN_CARD_IN_USE_LABELS } from './vcnCardStatus'

/** Ant Design Select does not handle `value: ''` reliably for "All" options. */
export const FILTER_ALL_VALUE = '__all__'

export function toFilterSelectValue<T extends string>(
  value: T | undefined
): T | typeof FILTER_ALL_VALUE {
  return value ?? FILTER_ALL_VALUE
}

export function fromFilterSelectValue<T extends string>(
  value: string | null | undefined
): T | undefined {
  if (value == null || value === '' || value === FILTER_ALL_VALUE) {
    return undefined
  }
  return value as T
}

/** Default drawer filters — active VCN cards only. */
export const DEFAULT_VCN_MANAGEMENT_FILTERS: VcnManagementBenefitFilters = {
  vcnStatus: VcnCardStatus.ACTIVE,
}

export function buildFiltersFromSidebarDraft(
  applied: VcnManagementBenefitFilters,
  draft: VcnManagementBenefitFilters
): VcnManagementBenefitFilters {
  return {
    search: applied.search,
    clientPublicKeys: draft.clientPublicKeys?.length
      ? draft.clientPublicKeys
      : undefined,
    carrierName: draft.carrierName || undefined,
    vcnProvider: draft.vcnProvider || undefined,
    vcnStatus: draft.vcnStatus || undefined,
    primaryPaymentMethod: draft.primaryPaymentMethod || undefined,
  }
}

export const PROVIDER_OPTIONS = [
  { value: FILTER_ALL_VALUE, label: 'All providers' },
  { value: 'WEX', label: 'Wex' },
  { value: 'MARQETA', label: 'Marqeta' },
] as const

export const STATUS_OPTIONS = [
  { value: FILTER_ALL_VALUE, label: 'All statuses' },
  { value: VcnCardStatus.ACTIVE, label: 'Active' },
  { value: VcnCardStatus.SUSPENDED, label: 'Suspended' },
  { value: VcnCardStatus.TERMINATED, label: 'Terminated' },
] as const

export const PAYMENT_METHOD_OPTIONS = [
  { value: FILTER_ALL_VALUE, label: 'All methods' },
  { value: 'ACH', label: 'ACH' },
  { value: 'VCN', label: 'VCN' },
  { value: 'Check', label: 'Check' },
  { value: 'GROUP ACH', label: 'GROUP ACH' },
] as const

/* ------------------------- Card Mode (EMS-9479) ------------------------- */

/**
 * Card Mode's defaults: active cards only, matching Benefit Mode's default.
 *
 * `inUse` is left unset rather than defaulting to IN_USE — the tab exists in
 * large part to find orphan cards, so opening it pre-filtered away from them
 * would hide the workflow.
 */
export const DEFAULT_VCN_MANAGEMENT_CARD_FILTERS: VcnManagementCardFilters = {
  cardStatus: VcnCardStatus.ACTIVE,
}

/**
 * `Plan` is a multi-select, because the Orphan tile is two of these values
 * (IN_USE_ENDED + NOT_IN_USE) and has to be expressible as one filter.
 */
export const IN_USE_OPTIONS = [
  VcnCardInUseStatus.IN_USE,
  VcnCardInUseStatus.IN_USE_ENDED,
  VcnCardInUseStatus.NOT_IN_USE,
].map((value) => ({ value, label: VCN_CARD_IN_USE_LABELS[value] }))

/**
 * Card Mode's status list. Same values as Benefit Mode's, different meaning:
 * here it is the CARD's own status, there it is the benefit's.
 */
export const CARD_STATUS_OPTIONS = [
  { value: FILTER_ALL_VALUE, label: 'All statuses' },
  { value: VcnCardStatus.ACTIVE, label: 'Active' },
  { value: VcnCardStatus.SUSPENDED, label: 'Suspended' },
  { value: VcnCardStatus.TERMINATED, label: 'Terminated' },
] as const

export function buildCardFiltersFromSidebarDraft(
  applied: VcnManagementCardFilters,
  draft: VcnManagementCardFilters
): VcnManagementCardFilters {
  return {
    search: applied.search,
    clientPublicKeys: draft.clientPublicKeys?.length
      ? draft.clientPublicKeys
      : undefined,
    carrierName: draft.carrierName || undefined,
    vcnProvider: draft.vcnProvider || undefined,
    cardStatus: draft.cardStatus || undefined,
    inUse: draft.inUse?.length ? draft.inUse : undefined,
  }
}

/**
 * How many drawer filters the admin has actually chosen.
 *
 * The default status does NOT count: Card Mode opens with `cardStatus: ACTIVE`,
 * so counting it would badge the Filters button with a 1 before anyone touched
 * it — and a badge that is never zero tells you nothing. Same rule Benefit Mode
 * applies to its own default (`filterVcnManagementRows.ts`).
 *
 * `search` does not count either: it lives in the toolbar, while this number
 * badges the Filters button.
 */
export function countVcnManagementCardActiveFilters(
  filters: VcnManagementCardFilters
): number {
  const { cardStatus: defaultCardStatus } = DEFAULT_VCN_MANAGEMENT_CARD_FILTERS
  const activeChecks: boolean[] = [
    (filters.clientPublicKeys?.length ?? 0) > 0,
    Boolean(filters.carrierName),
    Boolean(filters.vcnProvider),
    Boolean(filters.cardStatus) && filters.cardStatus !== defaultCardStatus,
    (filters.inUse?.length ?? 0) > 0,
  ]
  return activeChecks.filter(Boolean).length
}
